<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex flex-wrap items-center gap-2">
                        <h1 class="stage-page-title">Beziehungen</h1>
                        <div class="stage-shortcut-badges">
                            <span
                                v-for="badge in $navigationShortcutBadges('relationships')"
                                :key="badge.key"
                                :title="badge.title || 'Shortcut'"
                                class="stage-shortcut-badge"
                            >
                                <svg-vue class="stage-shortcut-badge-icon" icon="font-awesome.solid.command"></svg-vue>
                                {{ badge.label }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="stage-page-header-actions">
                    <button
                        id="company-directory-filter-button"
                        :aria-label="$t('Filter menu')"
                        class="btn-stage-icon-plain stage-filter-icon-button"
                        :title="$t('Filter menu')"
                        type="button"
                        @click="filtersSidebar = !filtersSidebar"
                    >
                        <span
                            v-if="hasActiveFilters"
                            aria-hidden="true"
                            class="stage-filter-active-dot"
                        ></span>
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.sliders"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="company-directory-surface w-full h-full bg-white shadow rounded relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>
                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <div
                        :class="{'company-directory-layout-empty': !loading && companies.length === 0}"
                        class="company-directory-layout h-full"
                    >
                        <section class="company-directory-master flex min-h-0 flex-col" aria-label="Unternehmensliste">
                            <relationships-hub-toolbar
                                active-section="companies"
                                :show-actions="canManageCompanies"
                                :show-view-switch="companies.length > 0"
                                :view-mode="viewMode"
                                @update:viewMode="setViewMode"
                            >
                                <template v-slot:actions>
                                    <router-link
                                        aria-label="Unternehmen anlegen"
                                        class="btn-stage-icon-plain"
                                        title="Unternehmen anlegen"
                                        to="/workspace/relationships/companies/new"
                                    >
                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.plus"></svg-vue>
                                    </router-link>
                                </template>
                            </relationships-hub-toolbar>
                            <div class="flex-1 min-h-0 overflow-auto">
                        <template v-if="companies.length > 0">
                            <div v-if="viewMode === 'list'" class="company-directory-list">
                                <article
                                    v-for="company in companies"
                                    :key="'company-list-' + company.id"
                                    :class="{'company-directory-entry-selected': company.id === selectedCompanyId}"
                                    tabindex="0"
                                    class="company-directory-entry"
                                    @click="selectCompany(company)"
                                    @keydown.enter.prevent="selectCompany(company)"
                                    @keydown.space.prevent="selectCompany(company)"
                                >
                                    <div class="company-directory-identity">
                                        <img
                                            :alt="company.name || 'Unternehmen'"
                                            :src="company.image"
                                            class="company-directory-avatar"
                                        >
                                        <div class="min-w-0">
                                            <div class="company-directory-title-row">
                                                <div class="company-directory-primary truncate">{{ company.name || '–' }}</div>
                                                <span
                                                    v-if="canShowCompanyRoleBadge"
                                                    class="company-directory-role-badge"
                                                >
                                                    {{ companyRoleLabel(company) }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="company-directory-cell">
                                        <div class="company-directory-employee-count">{{ companyEmployeeSummary(company) }}</div>
                                    </div>

                                    <div
                                        v-if="canManageCompanies || company.email || company.phone"
                                        class="company-directory-actions"
                                        @click.stop
                                    >
                                        <div class="relative inline-flex items-center justify-center">
                                            <button
                                                :aria-expanded="activeActionsCompanyId === company.id ? 'true' : 'false'"
                                                aria-haspopup="menu"
                                                aria-label="Aktionen"
                                                class="inline-flex h-7 w-7 items-center justify-center text-gray-500"
                                                title="Aktionen"
                                                type="button"
                                                @click.stop.prevent="toggleCompanyActions(company.id)"
                                            >
                                                <span class="flex flex-col items-center justify-center space-y-0.5">
                                                    <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                                    <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                                    <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                                </span>
                                            </button>
                                            <div
                                                v-if="activeActionsCompanyId === company.id"
                                                class="company-directory-actions-menu absolute right-0 top-full z-30 mt-2 overflow-hidden rounded border border-bordergray-100 bg-white shadow-sm"
                                                role="menu"
                                            >
                                                <button
                                                    v-if="canManageCompanies"
                                                    class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                    role="menuitem"
                                                    type="button"
                                                    @click.stop.prevent="editCompany(company)"
                                                >
                                                    Unternehmen bearbeiten
                                                </button>
                                                <a
                                                    v-if="company.email"
                                                    :href="`mailto:${company.email}`"
                                                    class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                    role="menuitem"
                                                    @click="closeCompanyActions"
                                                >
                                                    E-Mail senden
                                                </a>
                                                <a
                                                    v-if="company.phone"
                                                    :href="phoneHref(company.phone)"
                                                    class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                    role="menuitem"
                                                    @click="closeCompanyActions"
                                                >
                                                    Anrufen
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else aria-hidden="true"></div>
                                </article>
                            </div>

                            <div v-else class="company-directory-grid">
                                <article
                                    v-for="company in companies"
                                    :key="'company-grid-' + company.id"
                                    :class="{'company-directory-card-selected': company.id === selectedCompanyId}"
                                    tabindex="0"
                                    class="company-directory-card"
                                    @click="selectCompany(company)"
                                    @keydown.enter.prevent="selectCompany(company)"
                                    @keydown.space.prevent="selectCompany(company)"
                                >
                                    <div v-if="canManageCompanies" class="company-directory-card-actions" @click.stop>
                                        <button
                                            :aria-expanded="activeActionsCompanyId === company.id ? 'true' : 'false'"
                                            aria-haspopup="menu"
                                            aria-label="Aktionen"
                                            class="inline-flex h-7 w-7 items-center justify-center text-gray-500"
                                            title="Aktionen"
                                            type="button"
                                            @click.stop.prevent="toggleCompanyActions(company.id)"
                                        >
                                            <span class="flex flex-col items-center justify-center space-y-0.5">
                                                <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                                <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                                <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                            </span>
                                        </button>
                                        <div
                                            v-if="activeActionsCompanyId === company.id"
                                            class="company-directory-actions-menu absolute right-0 top-full z-30 mt-2 overflow-hidden rounded border border-bordergray-100 bg-white shadow-sm"
                                            role="menu"
                                        >
                                            <button
                                                class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                role="menuitem"
                                                type="button"
                                                @click.stop.prevent="editCompany(company)"
                                            >
                                                Unternehmen bearbeiten
                                            </button>
                                        </div>
                                    </div>
                                    <div class="company-directory-card-header">
                                        <img
                                            :alt="company.name || 'Unternehmen'"
                                            :src="company.image"
                                            class="company-directory-card-avatar"
                                        >
                                        <div class="min-w-0">
                                            <div class="company-directory-title-row">
                                                <div :title="company.name || '–'" class="company-directory-primary truncate">{{ company.name || '–' }}</div>
                                                <span
                                                    v-if="canShowCompanyRoleBadge"
                                                    class="company-directory-role-badge"
                                                >
                                                    {{ companyRoleLabel(company) }}
                                                </span>
                                            </div>
                                            <div
                                                v-if="companySecondarySummary(company)"
                                                :title="companySecondarySummary(company)"
                                                class="company-directory-secondary truncate"
                                            >
                                                {{ companySecondarySummary(company) }}
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        v-if="company.email || company.phone || companyAddress(company) !== '–' || companySocialLinks(company).length > 0"
                                        class="company-directory-card-details"
                                    >
                                        <div v-if="company.email" class="company-directory-card-line">
                                            <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.envelope"></svg-vue>
                                            <a
                                                :href="`mailto:${company.email}`"
                                                :title="company.email"
                                                class="company-directory-link min-w-0 truncate"
                                                @click.stop
                                            >
                                                {{ company.email }}
                                            </a>
                                        </div>
                                        <div v-if="company.phone" class="company-directory-card-line">
                                            <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.phone"></svg-vue>
                                            <a
                                                :href="phoneHref(company.phone)"
                                                :title="company.phone"
                                                class="company-directory-link min-w-0 truncate"
                                                @click.stop
                                            >
                                                {{ company.phone }}
                                            </a>
                                        </div>

                                        <div v-if="companyAddress(company) !== '–'" class="company-directory-card-line">
                                            <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.location-dot"></svg-vue>
                                            <a
                                                :href="companyAddressHref(company)"
                                                :title="companyAddress(company)"
                                                class="company-directory-link min-w-0 truncate"
                                                rel="noopener noreferrer"
                                                target="_blank"
                                                @click.stop
                                            >
                                                {{ companyAddress(company) }}
                                            </a>
                                        </div>

                                        <div v-if="companySocialLinks(company).length > 0" class="company-directory-social-links">
                                            <a
                                                v-for="link in companySocialLinks(company)"
                                                :key="link.key"
                                                :aria-label="link.label"
                                                :href="link.href"
                                                :title="link.label"
                                                class="company-directory-social-link"
                                                rel="noopener noreferrer"
                                                target="_blank"
                                                @click.stop
                                            >
                                                <svg-vue :icon="link.icon" class="h-3.5 w-3.5"></svg-vue>
                                            </a>
                                        </div>
                                    </div>
                                </article>
                            </div>
                        </template>

                        <div v-else-if="!loading" class="stage-empty-state">
                            <div class="stage-empty-state-inner">
                                <span class="stage-empty-state-icon-wrap">
                                    <svg-vue class="stage-empty-state-icon" icon="font-awesome.regular.magnifying-glass"></svg-vue>
                                </span>
                                <h3 class="stage-empty-state-title">Keine Unternehmen gefunden</h3>
                            </div>
                        </div>
                            </div>

                            <list-footer-pagination
                                :current-page="pagination.currentPage"
                                :end="paginationEnd"
                                :start="paginationStart"
                                :total="pagination.total"
                                :total-pages="pagination.totalPages"
                                nav-class="company-directory-pagination"
                                @next="changePage(pagination.currentPage + 1)"
                                @prev="changePage(pagination.currentPage - 1)"
                            />
                        </section>

                        <button
                            v-if="selectedCompany && mobileDetailOpen"
                            class="company-directory-detail-backdrop"
                            type="button"
                            aria-label="Detailansicht schließen"
                            @click="closeMobileDetail"
                        ></button>

                        <section
                            v-if="selectedCompany"
                            :class="{'company-directory-detail-mobile-open': mobileDetailOpen}"
                            class="company-directory-detail min-h-0"
                            aria-labelledby="company-directory-detail-title"
                        >
                            <div class="company-directory-detail-body">
                            <div class="company-directory-detail-toolbar">
                                <button
                                    aria-label="Detailansicht schließen"
                                    class="company-directory-detail-close"
                                    title="Zurück"
                                    type="button"
                                    @click="closeMobileDetail"
                                >
                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.chevron-right"/>
                                </button>
                                <span class="company-directory-detail-toolbar-title">Kontaktdetails</span>
                            </div>
                            <div class="company-directory-detail-header">
                                <img
                                    :alt="selectedCompany.name || 'Unternehmen'"
                                    :src="selectedCompany.image"
                                    class="company-directory-detail-avatar"
                                >
                                <div class="min-w-0 flex-1">
                                    <div class="company-directory-detail-title-row">
                                        <h2 id="company-directory-detail-title" class="text-xl font-semibold leading-tight text-gray-900">
                                            {{ selectedCompany.name || '–' }}
                                        </h2>
                                        <span
                                            v-if="canShowCompanyRoleBadge"
                                            class="company-directory-role-badge"
                                        >
                                            {{ companyRoleLabel(selectedCompany) }}
                                        </span>
                                    </div>
                                    <p
                                        v-if="companySecondarySummary(selectedCompany)"
                                        class="mt-0.5 text-sm leading-4 text-gray-500"
                                    >
                                        {{ companySecondarySummary(selectedCompany) }}
                                    </p>
                                </div>
                            </div>

                            <div class="company-directory-detail-section-header">
                                <h3 class="company-directory-detail-section-title">Kontakt</h3>
                            </div>

                            <div
                                v-if="hasSelectedCompanyContactData"
                                class="company-directory-contact-table"
                                role="table"
                            >
                                <div v-if="selectedCompany.email" class="company-directory-contact-row" role="row">
                                    <div class="company-directory-contact-label" role="rowheader">E-Mail</div>
                                    <div class="company-directory-contact-value" role="cell">
                                        <a :href="`mailto:${selectedCompany.email}`" class="company-directory-detail-link">
                                            {{ selectedCompany.email }}
                                        </a>
                                    </div>
                                </div>
                                <div v-if="selectedCompany.phone" class="company-directory-contact-row" role="row">
                                    <div class="company-directory-contact-label" role="rowheader">Telefon</div>
                                    <div class="company-directory-contact-value" role="cell">
                                        <a :href="phoneHref(selectedCompany.phone)" class="company-directory-detail-link">
                                            {{ selectedCompany.phone }}
                                        </a>
                                    </div>
                                </div>
                                <div v-if="selectedCompany.vat_id" class="company-directory-contact-row" role="row">
                                    <div class="company-directory-contact-label" role="rowheader">USt-ID</div>
                                    <div class="company-directory-contact-value" role="cell">
                                        {{ selectedCompany.vat_id }}
                                    </div>
                                </div>
                                <div v-if="selectedCompany.website" class="company-directory-contact-row" role="row">
                                    <div class="company-directory-contact-label" role="rowheader">Website</div>
                                    <div class="company-directory-contact-value" role="cell">
                                        <a
                                            :href="websiteHref(selectedCompany.website)"
                                            class="company-directory-detail-link"
                                            rel="noopener noreferrer"
                                            target="_blank"
                                        >
                                            {{ selectedCompany.website }}
                                        </a>
                                    </div>
                                </div>
                                <div v-if="selectedCompanySocialLinks.length > 0" class="company-directory-contact-row" role="row">
                                    <div class="company-directory-contact-label" role="rowheader">Social Media</div>
                                    <div class="company-directory-contact-value" role="cell">
                                        <span class="company-directory-detail-social-links">
                                            <a
                                                v-for="link in selectedCompanySocialLinks"
                                                :key="'company-detail-social-' + link.key"
                                                :aria-label="link.label"
                                                :href="link.href"
                                                :title="link.label"
                                                class="company-directory-detail-social-link"
                                                rel="noopener noreferrer"
                                                target="_blank"
                                            >
                                                <svg-vue :icon="link.icon" class="h-4 w-4"></svg-vue>
                                            </a>
                                        </span>
                                    </div>
                                </div>
                                <div
                                    v-if="companyAddress(selectedCompany) !== '–'"
                                    class="company-directory-contact-row"
                                    role="row"
                                >
                                    <div class="company-directory-contact-label" role="rowheader">Adresse</div>
                                    <div class="company-directory-contact-value" role="cell">
                                        <a
                                            :href="companyAddressHref(selectedCompany)"
                                            class="company-directory-detail-link"
                                            rel="noopener noreferrer"
                                            target="_blank"
                                        >
                                            {{ companyAddress(selectedCompany) }}
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <p v-else class="company-directory-detail-empty-text">Keine Kontaktdaten hinterlegt.</p>

                            <template v-if="canManageCompanies">
                                <div class="company-directory-detail-section-header">
                                    <h3 class="company-directory-detail-section-title">Mitarbeiter/innen</h3>
                                </div>

                                <div class="company-directory-detail-table-wrap">
                                    <table class="company-directory-detail-table company-directory-employees-table">
                                        <tbody>
                                            <tr
                                                v-for="user in companyAssignedUsers(selectedCompany)"
                                                :key="'company-employee-' + user.id"
                                            >
                                                <td>
                                                    <div class="company-directory-employee">
                                                        <img
                                                            :alt="user.name || 'Anwender'"
                                                            :src="companyAssignedUserAvatar(user)"
                                                            class="company-directory-employee-avatar"
                                                        >
                                                        <span class="company-directory-employee-name">{{ user.name || '–' }}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr v-if="companyAssignedUsers(selectedCompany).length === 0">
                                                <td class="company-directory-employees-empty">Keine Mitarbeiter/innen zugeordnet.</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </template>
                            </div>
                            <footer class="company-directory-detail-footer">
                                <span v-if="selectedCompany.updated_at" class="company-directory-detail-updated">
                                    <img
                                        :alt="companyUpdateActorName(selectedCompany)"
                                        :src="companyUpdateActorAvatar(selectedCompany)"
                                        class="company-directory-detail-updated-avatar"
                                    >
                                    <span>{{ companyUpdateActorName(selectedCompany) }}</span>
                                    <span>am {{ formatUpdatedAt(selectedCompany.updated_at) }}</span>
                                </span>
                                <button
                                    v-if="canManageCompanies"
                                    aria-label="Unternehmen bearbeiten"
                                    class="company-directory-detail-edit"
                                    title="Unternehmen bearbeiten"
                                    type="button"
                                    @click="editCompany(selectedCompany)"
                                >
                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.pen-to-square"/>
                                </button>
                            </footer>
                        </section>

                        <section v-else-if="companies.length > 0" class="company-directory-detail company-directory-detail-empty">
                            <div class="company-directory-detail-toolbar">
                                <span class="company-directory-detail-toolbar-title">Kontaktdetails</span>
                            </div>
                            <div class="company-directory-detail-empty-body">
                                <span class="stage-empty-state-icon-wrap">
                                    <svg-vue class="stage-empty-state-icon" icon="font-awesome.regular.magnifying-glass"></svg-vue>
                                </span>
                                <h2 class="stage-empty-state-title">Unternehmen auswählen</h2>
                            </div>
                        </section>
                    </div>

                    <aside
                        :aria-hidden="!filtersSidebar"
                        :class="filtersSidebar ? 'stage-filter-sidebar-open' : 'stage-filter-sidebar-closed'"
                        class="stage-filter-sidebar stage-filter-panel w-80 overflow-auto z-20"
                    >
                        <div class="stage-filter-body">
                            <div class="stage-filter-title-row">
                                <svg-vue class="stage-filter-title-icon h-5 w-5 text-gray-600 mr-3" icon="font-awesome.regular.sliders"></svg-vue>
                                <div class="stage-filter-title">{{ $t('Filter & Sorting') }}</div>
                            </div>

                            <div class="stage-filter-label">{{ $t('Sort by') }}</div>
                            <div class="stage-filter-row mb-3">
                                <button class="stage-filter-sort-btn text-sm leading-5 font-medium" type="button" @click="toggleSortOrder">
                                    <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.arrow-down-arrow-up"/>
                                </button>
                                <div class="stage-filter-sort-select-wrap">
                                    <select
                                        v-model="sort.column"
                                        class="stage-filter-sort-select block form-select text-sm leading-5 font-medium"
                                        @change="applyFilters"
                                    >
                                        <option value="name">Name</option>
                                        <option value="created_at">Erstellt am</option>
                                        <option value="updated_at">Zuletzt aktualisiert</option>
                                    </select>
                                    <span class="stage-filter-select-icon">
                                        <svg-vue class="h-[0.875rem] w-[0.875rem] text-gray-400" icon="font-awesome.regular.angle-down"></svg-vue>
                                    </span>
                                </div>
                            </div>

                            <div class="stage-filter-label">{{ $t('Search') }}</div>
                            <div class="mb-4">
                                <div class="relative">
                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.magnifying-glass"></svg-vue>
                                    </div>
                                    <input
                                        v-model.lazy="filters.search"
                                        class="stage-filter-input form-input block pl-10 text-sm"
                                        placeholder="Name, Ort oder Kontaktdaten"
                                        @change="applyFilters"
                                    >
                                </div>
                            </div>

                            <button
                                v-if="hasActiveFilters"
                                class="text-sm text-gray-500 hover:text-gray-600 focus:outline-none"
                                type="button"
                                @click="resetFilters"
                            >
                                {{ $t('Reset selection') }}
                            </button>
                        </div>
                    </aside>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import RelationshipsHubToolbar from '@/components/relationships/hub-toolbar';
import {resolveStoreAccess} from '@/utils/access-context';
import {
    queryNumber,
    queryString,
    queryWithoutKeys,
    routeWithReturnTo,
    sameRouteQuery,
    setQueryValue,
} from '@/utils/list-route-state';

const socialNetworks = [
    {key: 'linkedin', field: 'linkedin_url', label: 'LinkedIn', icon: 'font-awesome.brands.linkedin'},
    {key: 'xing', field: 'xing_url', label: 'XING', icon: 'font-awesome.brands.xing'},
    {key: 'instagram', field: 'instagram_url', label: 'Instagram', icon: 'font-awesome.brands.instagram'},
    {key: 'facebook', field: 'facebook_url', label: 'Facebook', icon: 'font-awesome.brands.facebook'},
];

function storedViewMode() {
    try {
        return window.localStorage.getItem('relationships.companies.view_mode') === 'grid' ? 'grid' : 'list';
    } catch (error) {
        return 'list';
    }
}

export default {
    name: 'workspace-relationships-companies',
    components: {
        RelationshipsHubToolbar,
    },
    data() {
        const routeQuery = this.$route && this.$route.query ? this.$route.query : {};
        const sortColumn = queryString(routeQuery, 'list_sort_column', 'name');

        return {
            companies: [],
            selectedCompanyId: null,
            mobileDetailOpen: false,
            activeActionsCompanyId: null,
            loading: false,
            filtersSidebar: false,
            viewMode: storedViewMode(),
            perPage: 20,
            filters: {
                search: queryString(routeQuery, 'list_search', ''),
            },
            sort: {
                column: ['name', 'created_at', 'updated_at'].includes(sortColumn) ? sortColumn : 'name',
                order: queryString(routeQuery, 'list_sort_order', 'asc') === 'desc' ? 'desc' : 'asc',
            },
            pagination: {
                currentPage: queryNumber(routeQuery, 'list_page', 1),
                perPage: 20,
                total: 0,
                totalPages: 1,
            },
            meta: {
                can_manage_companies: false,
                can_view_company_roles: false,
            },
        };
    },
    computed: {
        accessContext() {
            return resolveStoreAccess(this.$store);
        },
        canManageCompanies() {
            return !!this.meta.can_manage_companies
                && !!this.accessContext.is_admin
                && !!(this.accessContext.modules && this.accessContext.modules.companies);
        },
        canViewCompanyRoles() {
            return !!this.meta.can_view_company_roles;
        },
        canShowCompanyRoleBadge() {
            return this.canViewCompanyRoles && !!this.accessContext.is_admin;
        },
        selectedCompany() {
            return this.companies.find((company) => Number(company.id) === Number(this.selectedCompanyId)) || null;
        },
        selectedCompanySocialLinks() {
            return this.selectedCompany ? this.companySocialLinks(this.selectedCompany) : [];
        },
        hasSelectedCompanyContactData() {
            if (!this.selectedCompany) {
                return false;
            }

            return !!(
                this.selectedCompany.email
                || this.selectedCompany.phone
                || this.selectedCompany.vat_id
                || this.selectedCompany.website
                || this.selectedCompanySocialLinks.length > 0
                || this.companyAddress(this.selectedCompany) !== '–'
            );
        },
        hasActiveFilters() {
            return this.filters.search.trim() !== ''
                || this.sort.column !== 'name'
                || this.sort.order !== 'asc';
        },
        paginationStart() {
            if (this.pagination.total < 1) {
                return 0;
            }

            return ((this.pagination.currentPage - 1) * this.pagination.perPage) + 1;
        },
        paginationEnd() {
            return Math.min(this.pagination.currentPage * this.pagination.perPage, this.pagination.total);
        },
    },
    mounted() {
        this.getCompanies();
        document.addEventListener('click', this.handleDocumentClick);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleDocumentClick);
    },
    methods: {
        handleDocumentClick() {
            this.closeCompanyActions();
        },
        toggleCompanyActions(companyId) {
            this.activeActionsCompanyId = Number(this.activeActionsCompanyId) === Number(companyId)
                ? null
                : Number(companyId);
        },
        closeCompanyActions() {
            this.activeActionsCompanyId = null;
        },
        setViewMode(mode) {
            this.viewMode = mode === 'grid' ? 'grid' : 'list';
            try {
                window.localStorage.setItem('relationships.companies.view_mode', this.viewMode);
            } catch (error) {
                // The selected view still remains active for the current session.
            }
        },
        getCompanies() {
            this.loading = true;
            this.syncRouteState();

            return axios.get('api/workspace/relationships/companies', {
                params: {
                    page: this.pagination.currentPage,
                    perPage: this.perPage,
                    search: this.filters.search || undefined,
                    sort_column: this.sort.column,
                    sort_order: this.sort.order,
                },
            }).then((response) => {
                const data = response && response.data ? response.data : {};
                this.companies = Array.isArray(data.items) ? data.items : [];
                this.pagination = {
                    currentPage: Number(data.pagination && data.pagination.currentPage) || 1,
                    perPage: Number(data.pagination && data.pagination.perPage) || this.perPage,
                    total: Number(data.pagination && data.pagination.total) || 0,
                    totalPages: Math.max(1, Number(data.pagination && data.pagination.totalPages) || 1),
                };
                this.meta = {
                    can_manage_companies: !!(data.meta && data.meta.can_manage_companies),
                    can_view_company_roles: !!(data.meta && data.meta.can_view_company_roles),
                };
                if (!this.companies.some((company) => Number(company.id) === Number(this.selectedCompanyId))) {
                    this.selectedCompanyId = this.companies.length > 0 ? Number(this.companies[0].id) : null;
                }
            }).finally(() => {
                this.loading = false;
            });
        },
        applyFilters() {
            this.pagination.currentPage = 1;
            this.getCompanies();
        },
        resetFilters() {
            this.filters.search = '';
            this.sort = {
                column: 'name',
                order: 'asc',
            };
            this.pagination.currentPage = 1;
            this.getCompanies();
        },
        toggleSortOrder() {
            this.sort.order = this.sort.order === 'asc' ? 'desc' : 'asc';
            this.applyFilters();
        },
        changePage(page) {
            if (page < 1 || page > this.pagination.totalPages || page === this.pagination.currentPage) {
                return;
            }

            this.pagination.currentPage = page;
            this.getCompanies();
        },
        syncRouteState() {
            if (!this.$router || !this.$route) {
                return;
            }

            const removeKeys = ['list_page', 'list_sort_column', 'list_sort_order', 'list_search'];
            const nextQuery = queryWithoutKeys(this.$route.query || {}, removeKeys);
            setQueryValue(nextQuery, 'list_page', this.pagination.currentPage === 1 ? null : this.pagination.currentPage);
            setQueryValue(nextQuery, 'list_sort_column', this.sort.column === 'name' ? null : this.sort.column);
            setQueryValue(nextQuery, 'list_sort_order', this.sort.order === 'asc' ? null : this.sort.order);
            setQueryValue(nextQuery, 'list_search', this.filters.search.trim() || null);

            if (!sameRouteQuery(this.$route.query || {}, nextQuery)) {
                this.$router.replace({path: this.$route.path, query: nextQuery}).catch(() => {});
            }
        },
        selectCompany(company) {
            if (!company || Number(company.id) < 1) {
                return;
            }

            this.closeCompanyActions();
            this.selectedCompanyId = Number(company.id);
            this.mobileDetailOpen = true;
        },
        closeMobileDetail() {
            this.mobileDetailOpen = false;
        },
        editCompany(company) {
            if (!this.canManageCompanies || !company || Number(company.id) < 1) {
                return;
            }

            this.closeCompanyActions();
            const returnPath = String(this.$route.path || '').startsWith('/workspace')
                ? '/workspace/relationships/companies'
                : '/relationships/companies';
            this.$router.push(routeWithReturnTo(
                `/workspace/relationships/companies/${Number(company.id)}/edit`,
                this.$route,
                returnPath
            ));
        },
        companyRoleSummary(company) {
            return this.companyRoleWithAdministrationSummary(this.companyRoleLabel(company), company);
        },
        companySecondarySummary(company) {
            if (!this.canViewCompanyRoles) {
                return '';
            }

            if (!this.canShowCompanyRoleBadge) {
                return this.companyRoleSummary(company);
            }

            if (!Number.isFinite(Number(company.assigned_users_count))) {
                return '';
            }

            const count = Math.max(0, Number(company.assigned_users_count));
            return `${count} Anwender`;
        },
        companyEmployeeCount(company) {
            if (!company || !Number.isFinite(Number(company.assigned_users_count))) {
                return '–';
            }

            return Math.max(0, Number(company.assigned_users_count));
        },
        companyEmployeeSummary(company) {
            const count = this.companyEmployeeCount(company);

            if (count === '–') {
                return count;
            }

            return Number(count) === 1 ? '1 Mitarbeiter/in' : `${count} Mitarbeiter/innen`;
        },
        companyRoleLabel(company) {
            if (company.is_operator_company) {
                return 'Betreiber';
            }

            const roles = [];
            if (company.is_customer_company) {
                roles.push('Kunde');
            }
            if (company.is_provider_company) {
                roles.push('Dienstleister');
            }
            if (company.is_contact_company) {
                roles.push('Externer Kontakt');
            }

            return roles.join(' · ') || 'Unternehmen';
        },
        companyRoleWithAdministrationSummary(role, company) {
            if (!this.canManageCompanies || !Number.isFinite(Number(company.assigned_users_count))) {
                return role;
            }

            const count = Math.max(0, Number(company.assigned_users_count));
            return `${role} · ${count} Anwender`;
        },
        companyAssignedUsers(company) {
            if (!company || !Array.isArray(company.assigned_users)) {
                return [];
            }

            return company.assigned_users.filter((user) => user && Number(user.id) > 0);
        },
        companyAssignedUserAvatar(user) {
            if (user && user.avatar) {
                return user.avatar;
            }

            return '/images/default/icon.png';
        },
        companyAddress(company) {
            const streetAddress = [company.street, company.house_number].filter(Boolean).join(' ');
            const postalCity = [company.postal_code, company.city].filter(Boolean).join(' ');
            return [streetAddress, postalCity, company.country].filter(Boolean).join(', ') || '–';
        },
        formatRelativeDate(value) {
            if (!value) {
                return '–';
            }

            return moment(value).locale(window.app.app_date_locale || 'de').fromNow();
        },
        formatUpdatedAt(value) {
            if (!value) {
                return '–';
            }

            return this.$momentFormatDateTime(value).replace(/\s+(\d{1,2}:\d{2})$/, ', $1');
        },
        companyUpdateActorName(company) {
            return company.updated_by && company.updated_by.name
                ? company.updated_by.name
                : '';
        },
        companyUpdateActorAvatar(company) {
            const actor = company.updated_by;
            if (actor.avatar && actor.avatar !== 'gravatar') {
                return actor.avatar;
            }

            return actor.gravatar;
        },
        phoneHref(phone) {
            return `tel:${String(phone || '').replace(/[^\d+]/g, '')}`;
        },
        websiteHref(value) {
            return this.socialHref(value) || '#';
        },
        companyAddressHref(company) {
            return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(this.companyAddress(company))}`;
        },
        socialHref(value) {
            const normalized = String(value || '').trim();
            if (normalized === '') {
                return null;
            }

            const candidate = /^https?:\/\//i.test(normalized) ? normalized : `https://${normalized}`;
            try {
                const url = new URL(candidate);
                return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
            } catch (error) {
                return null;
            }
        },
        companySocialLinks(company) {
            return socialNetworks.reduce((links, network) => {
                const href = this.socialHref(company[network.field]);
                if (href) {
                    links.push({...network, href});
                }

                return links;
            }, []);
        },
    },
    metaInfo() {
        return {
            title: 'Beziehungen – Unternehmen',
        };
    },
};
</script>

<style scoped>
.company-directory-layout {
    display: grid;
    grid-template-columns: minmax(34rem, 3fr) minmax(25rem, 2fr);
    height: 100%;
    min-height: 0;
    overflow: hidden;
}

.company-directory-layout-empty {
    grid-template-columns: minmax(0, 1fr);
}

.company-directory-master {
    border-right: 1px solid var(--border-ultralight);
}

.company-directory-list {
    min-height: 100%;
}

.company-directory-pagination {
    background-color: var(--ui-surface-raised) !important;
}

.company-directory-entry {
    position: relative;
    display: grid;
    min-height: 5.5rem;
    grid-template-columns: minmax(18rem, 1fr) minmax(8rem, 0.35fr) 1.5rem;
    align-items: center;
    gap: 1.5rem;
    padding: 0.9rem 1.5rem;
    border-bottom: 1px solid var(--border-ultralight);
    background: #fff;
    cursor: pointer;
}

.company-directory-entry:nth-child(even) {
    background: var(--ui-surface-raised);
}

.company-directory-entry-selected::before,
.company-directory-card-selected::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 4px;
    background: var(--ui-text-secondary);
    content: '';
    pointer-events: none;
}

.company-directory-entry:focus,
.company-directory-card:focus {
    outline: none;
}

.company-directory-identity,
.company-directory-card-header,
.company-directory-card-line {
    display: flex;
    align-items: center;
    min-width: 0;
}

.company-directory-identity,
.company-directory-card-header {
    gap: 0.85rem;
}

.company-directory-avatar {
    width: 2.5rem;
    height: 2.5rem;
    flex: 0 0 2.5rem;
    border-radius: 0.25rem;
    object-fit: cover;
}

.company-directory-primary {
    color: var(--ui-text-secondary);
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25rem;
}

.company-directory-title-row,
.company-directory-detail-title-row {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 0.5rem;
}

.company-directory-detail-title-row {
    flex-wrap: wrap;
}

.company-directory-role-badge {
    display: inline-flex;
    min-height: 1.35rem;
    flex: 0 0 auto;
    align-items: center;
    padding: 0.1rem 0.45rem;
    border: 1px solid var(--ui-border-default);
    border-radius: 0.25rem;
    background: #fff;
    color: var(--ui-text-muted);
    font-size: 0.6875rem;
    font-weight: 500;
    line-height: 1rem;
    white-space: nowrap;
}

.company-directory-secondary {
    margin-top: 0.2rem;
    color: var(--ui-text-disabled);
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
}

.company-directory-employee-count {
    color: var(--ui-text-subtle);
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
    white-space: nowrap;
}

.company-directory-cell {
    min-width: 0;
}

.company-directory-link {
    color: var(--ui-text-secondary);
    cursor: pointer;
    text-decoration: none;
}

.company-directory-link:hover,
.company-directory-link:focus {
    text-decoration: none;
}

.company-directory-contact-value {
    color: var(--ui-text-muted);
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
}

.company-directory-contact-value + .company-directory-contact-value {
    margin-top: 0.125rem;
}

.company-directory-edit-indicator {
    color: var(--ui-border-strong);
}

.company-directory-actions {
    align-self: stretch;
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: flex-end;
}

.company-directory-actions-menu {
    width: max-content;
    white-space: nowrap;
    text-transform: none;
    letter-spacing: normal;
}

.company-directory-grid {
    display: grid;
    min-height: 100%;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 27rem), 1fr));
    align-content: start;
    gap: 1rem;
    padding: 1rem;
    background: #fff;
}

.company-directory-card {
    position: relative;
    display: flex;
    min-height: 10.75rem;
    flex-direction: column;
    padding: 1rem;
    border: 1px solid var(--border-ultralight);
    border-radius: 0.25rem;
    background: #fff;
    box-shadow: 0 1px 2px rgb(0 0 0 / 5%);
    cursor: pointer;
}

.company-directory-card-actions {
    position: absolute;
    z-index: 5;
    top: 1rem;
    right: 0.5rem;
    display: flex;
    height: 2.75rem;
    align-items: center;
}

.company-directory-card-actions + .company-directory-card-header {
    padding-right: 2rem;
}

.company-directory-card-avatar {
    width: 2.75rem;
    height: 2.75rem;
    flex: 0 0 2.75rem;
    border-radius: 0.25rem;
    object-fit: cover;
}

.company-directory-card-details {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.4rem;
    margin-top: 0.75rem;
    padding-top: 0.65rem;
    border-top: 1px solid var(--border-ultralight);
}

.company-directory-card-line {
    gap: 0.5rem;
    overflow: hidden;
    color: var(--ui-text-secondary);
    font-size: 0.8125rem;
    line-height: 1.25rem;
}

.company-directory-social-links {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    min-height: 1.25rem;
}

.company-directory-social-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--ui-text-disabled);
}

.company-directory-social-link:hover,
.company-directory-social-link:focus {
    color: var(--ui-text-secondary);
    outline: none;
}

.company-directory-detail-backdrop {
    display: none;
}

.company-directory-detail {
    display: flex;
    min-width: 0;
    height: 100%;
    max-height: 100%;
    flex-direction: column;
    overflow: hidden;
    background: #fff;
}

.company-directory-detail-body {
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 1.5rem 1.75rem 2rem;
}

.company-directory-detail-toolbar {
    position: sticky;
    top: -1.5rem;
    z-index: 10;
    display: flex;
    min-height: 3.25rem;
    align-items: center;
    gap: 0.75rem;
    flex-shrink: 0;
    margin: -1.5rem -1.75rem 1.5rem;
    padding: 0 1rem;
    border-bottom: 1px solid var(--border-ultralight);
    background: var(--ui-surface-raised);
}

.company-directory-detail-toolbar-title {
    min-width: 0;
    color: var(--ui-text-strong);
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25rem;
}

.company-directory-detail-close,
.company-directory-detail-edit {
    display: inline-flex;
    width: 2rem;
    height: 2rem;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    color: var(--ui-text-subtle);
    cursor: pointer;
}

.company-directory-detail-close {
    display: none;
}

.company-directory-detail-edit {
    margin-left: auto;
}

.company-directory-detail-close:hover,
.company-directory-detail-edit:hover {
    color: var(--ui-text-secondary);
}

.company-directory-detail-close:focus,
.company-directory-detail-edit:focus {
    outline: none;
}

.company-directory-detail-header {
    display: flex;
    min-height: 4.75rem;
    align-items: flex-start;
    gap: 1rem;
}

.company-directory-detail-avatar {
    width: 4.5rem;
    height: 4.5rem;
    flex: 0 0 4.5rem;
    border-radius: 0.25rem;
    object-fit: cover;
}

.company-directory-detail-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-top: 1.5rem;
}

.company-directory-detail-section-title {
    color: var(--ui-text-strong);
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.5rem;
}

.company-directory-contact-table {
    margin-top: 0.75rem;
    overflow: hidden;
    border: 1px solid var(--border-ultralight);
    border-radius: 0.25rem;
    background: #fff;
}

.company-directory-contact-row {
    display: grid;
    grid-template-columns: 7rem minmax(0, 1fr);
    align-items: center;
    border-bottom: 1px solid var(--border-ultralight);
}

.company-directory-contact-row:nth-child(even) {
    background: var(--ui-surface-raised);
}

.company-directory-contact-row:last-child {
    border-bottom: 0;
}

.company-directory-contact-label,
.company-directory-contact-value {
    padding: 0.65rem 1rem;
    text-align: left;
}

.company-directory-contact-label {
    color: var(--ui-text-subtle);
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
}

.company-directory-contact-value {
    min-width: 0;
    overflow-wrap: anywhere;
    color: var(--ui-text-secondary);
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
}

.company-directory-detail-table-wrap {
    display: block;
    margin-top: 0.75rem;
    padding: 0;
    overflow: hidden;
    border: 1px solid var(--border-ultralight);
    border-radius: 0.25rem;
    background: #fff;
}

.company-directory-detail-table {
    display: table;
    margin: 0 !important;
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    border-spacing: 0;
    vertical-align: top;
}

.company-directory-detail-table tr {
    border-bottom: 1px solid var(--border-ultralight);
}

.company-directory-detail-table tr:nth-child(even) {
    background: var(--ui-surface-raised);
}

.company-directory-detail-table tr:last-child {
    border-bottom: 0;
}

.company-directory-detail-table th,
.company-directory-detail-table td {
    padding: 0.65rem 1rem;
    text-align: left;
    vertical-align: middle;
}

.company-directory-detail-table th {
    width: 7rem;
    color: var(--ui-text-subtle);
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
}

.company-directory-detail-table td {
    overflow-wrap: anywhere;
    color: var(--ui-text-secondary);
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
}

.company-directory-employees-table td {
    padding-top: 0.55rem;
    padding-bottom: 0.55rem;
}

.company-directory-employee {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 0.75rem;
}

.company-directory-employee-avatar {
    width: 2rem;
    height: 2rem;
    flex: 0 0 2rem;
    border-radius: 9999px;
    object-fit: cover;
}

.company-directory-employee-name {
    min-width: 0;
    overflow: hidden;
    color: var(--ui-text-secondary);
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.company-directory-employees-empty {
    color: var(--ui-text-subtle) !important;
}

.company-directory-detail-link {
    color: inherit;
    text-decoration: none;
}

.company-directory-detail-link:hover,
.company-directory-detail-link:focus {
    color: var(--ui-text-strong);
    text-decoration: underline;
    text-underline-offset: 2px;
}

.company-directory-detail-social-links {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    vertical-align: middle;
}

.company-directory-detail-social-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--ui-text-disabled);
}

.company-directory-detail-social-link:hover,
.company-directory-detail-social-link:focus {
    color: var(--ui-text-secondary);
    outline: none;
}

.company-directory-detail-empty-text {
    margin-top: 0.75rem;
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-ultralight);
    border-radius: 0.25rem;
    color: var(--ui-text-subtle);
    font-size: 0.875rem;
}

.company-directory-detail-footer {
    display: flex;
    height: 3.5rem;
    min-height: 3.5rem;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    padding: 0 1.5rem;
    background: var(--ui-surface-raised);
    box-shadow: inset 0 1px 0 var(--border-ultralight);
}

.company-directory-detail-updated {
    display: inline-flex;
    min-width: 0;
    align-items: center;
    gap: 0.375rem;
    overflow: hidden;
    color: var(--ui-text-subtle);
    font-size: 0.75rem;
    line-height: 1rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.company-directory-detail-updated-avatar {
    width: 1.25rem;
    height: 1.25rem;
    flex: 0 0 1.25rem;
    border-radius: 9999px;
    object-fit: cover;
}

.company-directory-detail-empty {
    align-items: stretch;
    justify-content: flex-start;
    text-align: center;
}

.company-directory-detail-empty-body {
    display: flex;
    min-height: 0;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

@media (max-width: 1279px) {
    .company-directory-surface {
        overflow: hidden;
    }

    .company-directory-layout {
        position: relative;
        display: block;
        height: 100%;
        min-height: 0;
        overflow: hidden;
    }

    .company-directory-master {
        height: 100%;
        min-height: 0;
        border-right: 0;
        border-bottom: 0;
    }

    .company-directory-detail-backdrop {
        position: absolute;
        inset: 0;
        z-index: 20;
        display: block;
        width: 100%;
        height: 100%;
        padding: 0;
        border: 0;
        background: rgb(17 24 39 / 8%);
        cursor: pointer;
    }

    .company-directory-detail {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        z-index: 30;
        width: 92%;
        height: 100%;
        max-height: none;
        padding: 0;
        border-left: 1px solid var(--border-ultralight);
        background: #fff;
        box-shadow: -0.5rem 0 1.5rem rgb(0 0 0 / 12%);
        visibility: hidden;
        transform: translateX(100%);
        transition: transform 180ms ease-out, visibility 0s linear 180ms;
    }

    .company-directory-detail-body {
        padding: 1.25rem 1rem 1.75rem;
    }

    .company-directory-detail.company-directory-detail-mobile-open {
        visibility: visible;
        transform: translateX(0);
        transition-delay: 0s;
    }

    .company-directory-detail-toolbar {
        top: -1.25rem;
        margin: -1.25rem -1rem 1.25rem;
    }

    .company-directory-detail-close {
        display: inline-flex;
    }

    .company-directory-entry {
        grid-template-columns: minmax(0, 1fr) 1.5rem;
    }

    .company-directory-cell {
        display: none;
    }

    .company-directory-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 1023px) {
    .company-directory-entry {
        grid-template-columns: minmax(0, 1fr) 1.5rem;
    }

    .company-directory-grid {
        grid-template-columns: 1fr;
    }

}

@media (max-width: 639px) {
    .company-directory-entry {
        grid-template-columns: minmax(0, 1fr) 1.5rem;
        gap: 0.75rem;
        padding: 0.9rem 1rem;
    }

    .company-directory-cell {
        display: none;
    }

    .company-directory-grid {
        grid-template-columns: 1fr;
    }
}
</style>
